/**
 * Portuguese (pt-BR) — canonical dictionary.
 *
 * Source of truth: `en.ts` and `es.ts` are typed against `typeof pt`, so the
 * TypeScript build fails if any locale drifts from this shape.
 */

const pt = {
  meta: {
    htmlLang: 'pt-BR',
  },
  common: {
    loading: 'Carregando...',
    next: 'Próximo',
    finish: 'Finalizar',
    continue: 'Continuar',
    submit: 'Pagar',
    done: 'Pronto',
    copy: 'COPIAR',
    download: 'Download PDF',
    requiredField: 'Campo obrigatório!',
    invalidField: 'Campo inválido!',
  },
  languageSwitcher: {
    label: 'Idioma',
    pt: 'Português',
    en: 'Inglês',
    es: 'Espanhol',
  },
  errors: {
    pixExpired: 'Pix expirou! Realize uma nova compra.',
    purchaseFailed: 'Falha na compra',
    portabilityFailed: 'Falha na solicitação de portabilidade',
    portabilityUndefined: 'Falha na solicitação de portabilidade: resposta indefinida',
    cardRegisterUnknown: 'Erro desconhecido ao cadastrar cartão',
    purchaseUnknown: 'Erro na requisição de compra',
    invalidCoupon: 'Digite um cupom válido!',
    cepNotFound: 'CEP não encontrado. Confira o número digitado.',
    generic: 'Não foi possível processar a requisição. Tente novamente.',
    noConnection: 'Falha de conexão. Verifique sua internet e tente novamente.',
    byStatus: {
      400: 'Requisição inválida.',
      401: 'Sessão não autenticada. Recarregue a página.',
      402: 'Pagamento recusado. Verifique os dados do cartão ou tente outro.',
      403: 'Acesso negado.',
      404: 'Recurso não encontrado.',
      405: 'Operação não permitida.',
      409: 'Esta operação não está mais disponível.',
      422: 'Dados informados estão inválidos ou incorretos.',
      429: 'Muitas tentativas. Aguarde alguns instantes.',
      500: 'Erro interno no servidor. Tente novamente.',
      502: 'Serviço temporariamente indisponível. Tente novamente.',
      503: 'Serviço temporariamente indisponível. Tente novamente.',
    },
    retryAfter: 'Aguarde {{segundos}} segundos e tente novamente.',
    paymentDeclined: 'Pagamento recusado. Verifique os dados do cartão.',
    paymentUnavailable: 'Pagamento temporariamente indisponível. Tente novamente mais tarde.',
    paymentAttemptsOver: 'Sessão esgotada por muitas tentativas de pagamento.',
    purchaseExpired: 'Esta compra expirou ou não foi validada. Refaça a compra.',
    summaryUnavailable: 'Não foi possível carregar o resumo da compra.',
    planUnavailable: 'O plano escolhido não está mais disponível. Escolha outro plano para continuar.',
    threeDSTimeout: 'A autenticação do seu banco não respondeu. Tente novamente — seus dados de compra continuam válidos.',
    threeDSUnavailable: 'Não foi possível carregar a verificação de segurança do pagamento. Recarregue a página; se o problema continuar, entre em contato pelo e-mail ti@conteltelecom.com.br.',
    threeDSDeclined: 'Autenticação 3DS recusada pelo seu banco. Tente outro cartão.',
    threeDSTechnicalError: 'Erro técnico na autenticação 3DS. Tente novamente.',
    threeDSStartFailed: 'Não foi possível iniciar a autenticação 3DS. Recarregue a página.',
    threeDSUnenrolled: 'Não foi possível autenticar este cartão no seu banco. Verifique os dados ou use outro cartão.',
    threeDSUnsupportedBrand: 'A bandeira deste cartão não é aceita na autenticação. Use um cartão de outra bandeira.',
    recaptchaFailed: 'Erro na validação do recaptcha. Tente novamente!',
  },
  snackbar: {
    pixCopied: 'Pix copiado!',
    codeCopied: 'Código copiado!',
  },
  page404: {
    notFound: 'Página não encontrada',
  },
  page1: {
    sloganDefault: 'Turbine seu celular com a maior velocidade',
    uncomplicateDefault: 'Descomplica',
    detailsDefault1: 'Faça como mais de 123mil usuários',
    detailsDefault2: 'Temos o plano certo para você',
    bestSellersDefault1: 'Faça como os melhores',
    bestSellersDefault2: 'Escolha um plano que é sua cara',
    chooseProfile1: 'Você escolhe seu perfil',
    chooseProfile2: 'Feito para você, na medida',
    perfilLabel: 'PERFIL',
    choosePlanTitle: 'Escolha seu plano',
    choosePlanSubtitle: 'Você decide quanto de internet você precisa',
    chipAvulsoFallback: 'avulso',
    chipAvulsoDescription: 'Compre agora o chip e ative seu plano depois',
    bnftsTitle: 'Benefícios do Chip',
    whatsappFreeHtml: 'Whasapp<br/> Grátis',
    callsInfiniteHtml: 'Ligações<br/> Infinitas',
    wazeUnlimitedHtml: 'Waze<br/> Ilimitado',
    activatedBnfts: {
      title: 'Benefícios Ativados',
      portabilityActivated: 'Portabilidade Ativada',
      moreInternet: 'Mais internet pelo mesmo valor',
      bonusGB: '+5GB Grátis',
    },
    planDetails: {
      whatsapp: 'WhatsApp liberado',
      callsUnlimited: 'Ligações ilimitadas',
      callsLimited: '60min de ligações',
      net5G: 'Rede 5G',
      internetAccumulate: 'Internet que acumula',
      noCutsSuffix: 'sem cortes', // used as `${plan.plano} ${noCutsSuffix}`
      nationalCoverage: 'Cobertura nacional',
      wazeUnlimited: 'Waze ilimitado',
      mobileIncluded: 'Incluso no seu plano',
      mobileTechnology: 'Tecnologia',
      mobileAccumulate: 'Acumule',
      mobileInternetAccumulation: 'Acúmulo de internet',
      mobileNoCuts: 'Sem Cortes',
      mobileInternet: 'Internet',
      mobileBonus: 'Bônus',
      mobileMonth: 'Todo mês',
    },
    selectPlanDetails: {
      portabilityOn: 'Trazer meu número de outra operadora',
      portabilityOff: 'Trazer meu número de outra operadora',
      gainingInternet: 'Você está ganhando mais internet',
      losingInternet: 'Você está perdendo internet',
      losingBadge: 'PERDER 5GB',
      gainingBadge: 'GANHAR 5GB',
      mostPopular: 'MAIS PROCURADO',
      renewMonthly: 'Renove todo mês sem fidelidade',
      mostProfitable: 'MAIS RENTÁVEL',
      buyTwelveForEight: 'Pague 8 planos e leve 12',
      chipPhysical: 'Chip físico',
      becomeClient: 'Para virar cliente',
      becomeClientFallback: 'Para virar nosso cliente',
      monthly: 'Mensal',
      annual: 'Anual',
    },
    invite: {
      inviteFrom: 'Você recebeu um convite de',
      toBePartOf: 'para fazer parte da',
      configureYourPlan: 'CONFIGURE SEU PLANO',
    },
    voucher: {
      iHaveCode: 'Tenho um código',
      brandClub: '{{brand}} Club',
      digitCode: 'Digite o código de indicação',
      placeholder: '#FE2025',
      validate: 'VALIDAR',
      invalidCode: 'Digite um voucher válido!',
      validCode: 'Cupom válido!',
    },
    timer: {
      limitedTime: 'Tempo Limitado',
      gainBonus: 'Ganhe 5 GB trazendo seu número',
    },
    slider: {
      internetAmount: 'Quantidade de Internet',
    },
  },
  page2: {
    headingDefaultTitles: {
      step0: 'Configure Seu Plano',
      step0Sub: 'Escolha mais opções e aprimore seu plano',
      formStep: 'Informações Pessoais',
      formStepSub: 'Preencha os campos obrigatórios',
      paymentStep: 'Escolha como deseja pagar',
      paymentStepSub: 'Escolha mais opções e aprimore seu plano',
      step4: 'Pagamento',
      step4Sub: 'Conclua o pagamento da sua compra',
    },
    stepper: {
      yourPlan: 'Seu Plano',
      info: 'Informações',
      address: 'Endereço',
      paymentMethod: 'Método de pagamento',
      payment: 'Pagamento',
    },
    chipInfo: {
      physicalChipHtml: 'CHIP Físico <span>(Compra Única)</span>',
      physicalChipDesc: 'Chip que você insere em seu aparelho',
      receiveAtHome: 'Receber em casa',
      receiveAtHomeDesc: 'Receba o chip no seu endereço',
      pickUpInStore: 'Retirar na loja',
      pickUpInStoreDesc: 'Retire o chip na loja mais perto',
      virtualChip: 'CHIP Virtual',
      virtualChipDesc: 'Chip para celulares modernos (Ex. Iphone 12)',
    },
    pickUpChip: {
      atHome: 'Receber em casa',
      atHomeDesc: 'Receba o chip no seu endereço',
      atStore: 'Retirar na loja',
      atStoreDesc: 'Ver no mapa',
    },
    sectionTitles: {
      chipReceive: 'Recebimento do chip físico',
      chipReceiveSub: 'Escolha como o chip chegará até você',
      accessTechnology: 'Tecnologia de acesso',
      accessTechnologySub: 'Como você vai se conectar',
      bonusGain: 'Ganhe Bônus',
      bonusGainSub: 'Veja como aumentar sua internet',
    },
    smartControl: {
      title: 'Contrato Inteligente',
      subtitle: 'Mesmo valor com mais internet',
      bonusBadge: '+5GB GRÁTIS',
    },
    personalInfo: {
      title: 'Informações Pessoais',
      subtitle: 'Como podemos te identificar',
      fullName: 'Nome Completo',
      cpfCnpj: 'CPF ou CNPJ',
      birthdate: 'Data de Nascimento',
    },
    contactInfo: {
      title: 'Informações de Contato',
      subtitle: 'Como podemos falar com você',
      email: 'E-mail',
      ddd: 'DDD',
      phone: 'Telefone',
    },
    addressInfo: {
      title: 'Informações de Endereço',
      subtitle: 'Onde podemos te encontrar',
      cep: 'CEP',
      state: 'Estado',
      city: 'Cidade',
      street: 'Rua',
      neighborhood: 'Bairro',
      number: 'Número',
      complement: 'Complemento',
      reference: 'Referência',
    },
    payment: {
      oneTimeTitle: 'Pagamento à vista',
      oneTimeSubtitle: 'Selecione uma das opções de pagamento à vista',
      installmentTitle: 'Pagamento parcelado',
      installmentSubtitle: 'Pague em até 12x pelo nosso site de pagamentos',
      finalizeInstallmentTitle: 'Finalize sua compra parcelada',
      finalizeInstallmentSubtitle: 'Clique em "Pagar" e você será redirecionado para concluir o pagamento.',
      cardTitle: 'Cartão de Crédito',
      cardSubtitle: 'Aceitamos todos os cartões',
      cardBadge: 'RECOMENDADO',
      cardNumber: 'Número do Cartão',
      cardName: 'Nome Completo',
      cardCpf: 'CPF do Titular do Cartão',
      cardExpiry: 'Data de Vencimento',
      cardCvv: 'Código de Segurança',
      keepRecurring: 'Manter recorrência no cartão de crédito',
      pixTitle: 'PIX',
      pixSubtitle: 'Pague com o app do seu banco',
      pixBadge: 'PAGAMENTO IMEDIATO',
      pixStep1Title: 'Abra o app do seu banco',
      pixStep1Desc: 'Você pode pagar com seu banco de preferência.',
      pixStep2Title: 'Escanei ou digite a chave PIX',
      pixStep2Desc: 'Aponte a camera do seu celular para a imagem ao lado ou copie o código',
      pixStep3Title: 'Pronto! Aguarde a confirmação',
      pixStep3Desc: 'Assim que confirmado você será direcionado para a próxima página',
      pixExpiresIn: 'EXPIRA EM: ',
      pixTimeRemaining: 'Tempo restante',
      boletoTitle: 'Boleto Bancário',
      boletoSubtitle: 'Até 2 dias úteis',
      boletoBadge: 'Pague com boleto',
      boletoStep1Title: 'Copie o código e pague com seu banco',
      boletoStep1Desc: 'Você pode pagar com seu banco de preferência.',
      wppFinalizeTitle: 'Finalize o pagamento pelo WhatsApp!',
      wppFinalizeDesc: 'Clique em "Continuar" e finalize o pedido com um de nossos atendentes.',
      paymentApproved: 'Pagamento aprovado! Redirecionando...',
      unixCardDescription: 'Utilize o seu cartão Unix para pagar a compra',
      redoPurchase: 'Refazer compra',
      paymentDeadlineExpired: 'O prazo para pagamento expirou. Refaça a compra.',
      payWithin: 'Conclua o pagamento em {{tempo}}',
    },
    summary: {
      total: 'Total desta compra',
      orderTitle: 'Resumo do seu pedido',
      orderSubtitle: 'Detalhes do seu pedido',
      secondTitle: 'Resumo do seu pedido',
      secondSubtitle: 'Tudo que está incluso em seu pedido conosco',
      bonusTitle: 'Bônus do Plano',
      bonusSubtitle: 'Ganhe mais internet no seu plano',
      portability: 'Portabilidade',
      portabilityDesc: 'Troque de operadora e mantenha o número',
      portabilityBonus: '+5GB GRÁTIS',
      smartControl: 'Controle Inteligente',
      smartControlDesc: 'Pagamento mensal com fidelidade de 12 meses',
      chipPhysical: 'Chip Físico',
      chipVirtual: 'Chip Virtual',
      chipValue: 'Valor Chip',
      chipPaymentOnce: 'Pagamento Único',
      chipDigitalDelivery: 'Entrega Digital',
      shipping: 'Frete',
      shippingDesc: 'Envio imediato',
      paymentMonthly: 'Pagamento mensal',
      paymentOneTime: 'Pagamento à vista',
      chipUnit: 'chip',
      chipUnitPlural: 'chips',
    },
    coupon: {
      title: 'Descontos',
      remove: 'Remover',
      active: 'Cupom ativo',
      codeLabel: 'Código de Desconto',
      activate: 'Ativar',
      activateLong: 'Ativar Cupom',
    },
  },
  page3: {
    welcomeGreeting1: 'Bem-vindo à',
    welcomeGreeting2: 'Connect,',
    welcomeDescription: 'Agora você faz parte da revolução da comunicação no Brasil! Aproveite seu novo plano.',
    nextStepsTitle: 'Próximos Passos',
    nextStepsSubtitle: 'Termine de configurar sua conta aqui',
    plan: {
      monthlyTitle: 'Seu Plano Mensal',
      annualTitle: 'Seu Plano Anual',
      monthlyDesc: 'Veja quanto de internet mensal você tem',
      smartControlBadge: '+5GB CONTRATO',
      portabilityBadge: '+5GB PORTABILIDADE',
    },
    stepper: {
      whatsappHelp: 'Preciso de ajuda',
      esim: 'eSIM',
      installEsim: 'Instalação do eSIM',
      installEsimDesc: 'Acesse o PDF abaixo para iniciar o processo de configuração',
      portabilityTitle: 'PORTABILIDADE',
      schedulePortability: 'Agendar Portabilidade',
      portabilityWarning: 'Não se preocupe, o processo só será iniciado assim que o seu chip Contel for ativado.',
      whichPhoneTransfer: 'Qual telefone deverá ser transferido para a Contel?',
      whichOperator: 'Este número atualmente é de qual operadora?',
      confirmEverything: 'Confirme que está tudo certo',
      brandFallback: 'Connect',
      leaving: 'Saindo',
      entering: 'Entrando',
      attention: 'Atenção',
      attentionText: 'Ao finalizar confirmo que este número está ativo e também registrado em meu CPF ou CNPJ',
      allDone: 'Tudo certo',
      portabilityProcessNote: 'Em 03 dias úteis depois da confirmação do pedido de portabilidade, seu número será migrado.',
      esimInstructions: 'Clique em Finalizar para confirmar seu pedido de portabilidade',
      chipInstructions: 'Quando seu CHIP Físico chegar, ative-o normalmente',
      followingSmsConfirm: 'Em seguida, responda SIM para o SMS de confirmação que vai chegar em instantes',
      buildYourPlan: 'Montar seu plano',
      buildYourPlanDone: 'Plano concluído com sucesso',
      installEsimTitle: 'Instalar eSIM',
      installEsimComplete: 'Complete a instalação do CHIP',
      scheduleTitle: 'Agendar Portabilidade',
      scheduleSubtitlePrefix: 'Traga seu número para a',
      scheduleSubtitleFallbackBrand: 'nossa operadora',
      smartControlTitle: 'Controle  Inteligente',
      smartControlSubtitle: 'Configure seu contrato',
      doubts: 'Dúvidas?',
      doubtsSubtitle: 'Atendimento 24h pelo WhatsApp',
    },
    smartControlModal: {
      title: 'CONTROLE INTELIGENTE',
      done: 'PRONTO',
    },
  },
  page4: {
    downloadApp: 'Baixe nosso app para ficar por dentro de tudo!',
    slogan1: 'Tudo certo,',
    slogan2: 'pode',
    slogan3: 'aproveitar sua conta',
    sloganVirtual1: 'Você receberá um',
    sloganVirtual2: 'SMS',
    sloganVirtual3: 'para configurar seu plano',
  },
  footer: {
    titleDefault: '#Descomplica com a',
    aboutPrefix: 'SOBRE A',
    askDoubts: 'TIRE SUAS DÚVIDAS',
    lgpd: 'LGPD',
    contact: 'FALE CONOSCO',
    copyrightPrefix: '© Grupo',
    copyrightFallbackBrand: 'Contel',
    copyrightSuffix: 'Todos os direitos reservados.',
  },
};

export default pt;
